/**
 * Tracks the OPC schedule imports a user has kicked off from the Schedules in
 * OPC grid, from the moment the export POST is accepted until the import_log it
 * created reaches a terminal status.
 *
 * Separate from opc-sync-state deliberately: that one is a single slot for the
 * project-level sync, so tracking a row import there would evict a running sync
 * (and vice versa). This one is keyed by schedule, because several rows can be
 * imported at once and each needs its own spinner.
 *
 * Leaf module — no imports — so it can be shared by the grid renderers, the
 * page and the watcher without creating import cycles.
 *
 * Mirrored to sessionStorage so a reload mid-import still resolves the row.
 * sessionStorage rather than localStorage: per-tab, so two tabs cannot both
 * announce the same import.
 */

export type TOpcExportHandle = {
  /** schedule_source_info.id — also the grid's row id. */
  scheduleId: string;
  /** Guards against acting on one project's import while viewing another. */
  projectId: string;
  /** Returned by the export POST; what the status poll is correlated on. */
  importLogId: string;
  /** Epoch ms. Drives the cap from wall-clock, so it survives a reload. */
  startedAt: number;
};

export const OPC_EXPORT_STORAGE_KEY = "ciq:opc-exports";

/**
 * Longest an import is tracked. Past this the handle is dropped and the row
 * falls back to whatever the server data says, rather than spinning forever.
 */
export const OPC_EXPORT_TIMEOUT_MS = 15 * 60 * 1000;

export const isOpcExportExpired = (handle: TOpcExportHandle): boolean =>
  Date.now() - handle.startedAt >= OPC_EXPORT_TIMEOUT_MS;

export type TOpcExportHandles = Record<string, TOpcExportHandle>;

type OpcExportListener = (handles: TOpcExportHandles) => void;

let current: TOpcExportHandles = {};
let hydrated = false;
const listeners = new Set<OpcExportListener>();

const isValidHandle = (value: any): boolean =>
  Boolean(
    value?.scheduleId &&
    value?.projectId &&
    value?.importLogId &&
    typeof value.startedAt === "number"
  );

const readFromSession = (): TOpcExportHandles => {
  try {
    const raw = sessionStorage.getItem(OPC_EXPORT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};

    // Anything incomplete or already past the cap is dropped rather than
    // trusted — this value survives reloads and deploys.
    return Object.values(parsed as TOpcExportHandles).reduce(
      (kept: TOpcExportHandles, handle) => {
        if (isValidHandle(handle) && !isOpcExportExpired(handle)) {
          return { ...kept, [handle.scheduleId]: handle };
        }
        return kept;
      },
      {}
    );
  } catch {
    // Unparseable, or sessionStorage unavailable (private mode).
    return {};
  }
};

const writeToSession = (handles: TOpcExportHandles) => {
  try {
    if (Object.keys(handles).length) {
      sessionStorage.setItem(OPC_EXPORT_STORAGE_KEY, JSON.stringify(handles));
    } else {
      sessionStorage.removeItem(OPC_EXPORT_STORAGE_KEY);
    }
  } catch {
    // Storage is a convenience here; the in-memory handles still work without it.
  }
};

const emit = () => {
  listeners.forEach((cb) => {
    try {
      cb(current);
    } catch {
      // a failing listener must not stop the others from being told
    }
  });
};

export function getOpcExports(): TOpcExportHandles {
  if (!hydrated) {
    hydrated = true;
    current = readFromSession();
    // Rewrite so anything rejected on hydrate cannot be re-read later.
    writeToSession(current);
  }
  return current;
}

export function getOpcExport(scheduleId: string): TOpcExportHandle | undefined {
  return getOpcExports()[scheduleId];
}

export function markOpcExportStarted(handle: TOpcExportHandle): void {
  current = { ...getOpcExports(), [handle.scheduleId]: handle };
  writeToSession(current);
  emit();
}

export function clearOpcExport(scheduleId: string): void {
  // Idempotent: clearing an untracked schedule must not wake listeners, or a
  // StrictMode double-effect would re-render every cell twice.
  if (!getOpcExports()[scheduleId]) return;
  current = Object.fromEntries(
    Object.entries(current).filter(([id]) => id !== scheduleId)
  );
  writeToSession(current);
  emit();
}

export function onOpcExportsChange(cb: OpcExportListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
